/**
 * Bulk members: add or remove people as members of many projects at once.
 *
 * Pure core, no React: `planBulkChanges` turns the request into server calls
 * plus known no-ops (from the members read before the run), `runBulkCalls`
 * runs the calls, `summariseResults` words the outcome.
 *
 * Server facts this relies on: one person on one project per call; adding an
 * existing member and removing a non-member both succeed, while every add
 * failure (owner, unknown user, archived project) gives the same generic
 * error, so skips are decided here from the members list, not from replies.
 * The members file has no locking, so calls on one project never overlap.
 */

export type BulkAction = "add" | "remove"

export interface BulkProject {
  id: string
  name: string
  owner: string
}

/** Members read before the run; an Error when the list couldn't be read. */
export type MembersByProject = Record<string, string[] | Error>

export type SkipReason = "already-member" | "owner" | "not-member"

export type Outcome = "done" | "failed" | "not-run" | SkipReason | "unreadable"

export interface BulkCall {
  projectId: string
  projectName: string
  person: string
}

export interface BulkResult extends BulkCall {
  outcome: Outcome
  error?: string
}

const UNREADABLE_ERROR = "Couldn't read its members"
const DEFAULT_CONCURRENCY = 4

/** The server drops duplicate members ignoring case, so names compare the same way. */
export const sameName = (name: string): string => name.trim().toLowerCase()

/** Drops repeats (by `key`), keeping the first. */
const uniqueBy = <T>(items: T[], key: (item: T) => string): T[] => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const k = key(item)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/**
 * The members of a project as a set of compared names, or an Error when the
 * list couldn't be read (a project missing from `members` counts as unread).
 */
const memberSet = (
  members: MembersByProject,
  projectId: string
): Set<string> | Error => {
  const list = members[projectId]
  if (list instanceof Error) return list
  if (!list) return new Error(UNREADABLE_ERROR)
  return new Set(list.map(sameName))
}

/**
 * Splits a request into the calls to make and the results already known.
 *
 * Add: the project's owner is skipped (`owner`, the server refuses it), a
 * member is skipped (`already-member`), an unreadable project gives one
 * `unreadable` result per person. Remove: the owner is never planned (no
 * result at all), a non-member is skipped (`not-member`), an unreadable
 * project gives `unreadable`.
 */
export const planBulkChanges = (
  action: BulkAction,
  people: string[],
  projects: BulkProject[],
  members: MembersByProject
): { calls: BulkCall[]; skipped: BulkResult[] } => {
  const calls: BulkCall[] = []
  const skipped: BulkResult[] = []
  const uniquePeople = uniqueBy(people, sameName)

  for (const project of uniqueBy(projects, (p) => p.id)) {
    const current = memberSet(members, project.id)
    const owner = sameName(project.owner)

    for (const person of uniquePeople) {
      const call: BulkCall = {
        projectId: project.id,
        projectName: project.name,
        person,
      }
      const isOwner = sameName(person) === owner

      if (action === "add" && isOwner) {
        skipped.push({ ...call, outcome: "owner" })
      } else if (action === "remove" && isOwner) {
        continue
      } else if (current instanceof Error) {
        skipped.push({ ...call, outcome: "unreadable", error: UNREADABLE_ERROR })
      } else if (action === "add" && current.has(sameName(person))) {
        skipped.push({ ...call, outcome: "already-member" })
      } else if (action === "remove" && !current.has(sameName(person))) {
        skipped.push({ ...call, outcome: "not-member" })
      } else {
        calls.push(call)
      }
    }
  }

  return { calls, skipped }
}

/**
 * For each person, how many of the projects they're a member of. Being the
 * owner doesn't count; unreadable projects count for no one.
 */
export const memberCounts = (
  people: string[],
  projects: BulkProject[],
  members: MembersByProject
): Record<string, number> => {
  const counts: Record<string, number> = {}
  for (const person of people) counts[person] = 0

  for (const project of uniqueBy(projects, (p) => p.id)) {
    const current = memberSet(members, project.id)
    if (current instanceof Error) continue
    const owner = sameName(project.owner)
    for (const person of people) {
      const name = sameName(person)
      if (name !== owner && current.has(name)) counts[person] += 1
    }
  }

  return counts
}

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

/**
 * Runs the calls: those on one project one after another, at most
 * `concurrency` projects at once. A failed call is recorded and the rest go
 * on. Once `signal.stopped` is set, calls not yet started end as `not-run`.
 * `onProgress(done, total)` follows every settled call; not-run calls are
 * counted as settled once, at the end. Results keep the order of `calls`.
 */
export const runBulkCalls = async (
  calls: BulkCall[],
  send: (call: BulkCall) => Promise<void>,
  opts: {
    concurrency?: number
    signal?: { stopped: boolean }
    onProgress?: (done: number, total: number) => void
  } = {}
): Promise<BulkResult[]> => {
  const { signal, onProgress } = opts
  const concurrency = Math.max(1, opts.concurrency ?? DEFAULT_CONCURRENCY)
  const total = calls.length
  const results: BulkResult[] = calls.map((call) => ({
    ...call,
    outcome: "not-run",
  }))

  // Indexes into `calls`, grouped by project in first-seen order
  const groups = new Map<string, number[]>()
  calls.forEach((call, index) => {
    const group = groups.get(call.projectId)
    if (group) group.push(index)
    else groups.set(call.projectId, [index])
  })
  const queue = [...groups.values()]

  let done = 0
  const runGroup = async (indexes: number[]): Promise<void> => {
    for (const index of indexes) {
      if (signal?.stopped) return
      try {
        await send(calls[index])
        results[index] = { ...calls[index], outcome: "done" }
      } catch (error) {
        results[index] = {
          ...calls[index],
          outcome: "failed",
          error: errorMessage(error),
        }
      }
      done += 1
      onProgress?.(done, total)
    }
  }

  const worker = async (): Promise<void> => {
    for (let group = queue.shift(); group; group = queue.shift()) {
      await runGroup(group)
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, worker)
  )

  if (done < total) onProgress?.(total, total)
  return results
}

const projects = (count: number): string =>
  `${count} ${count === 1 ? "project" : "projects"}`

/**
 * The summary lines for a finished run, in a fixed order, leaving out zero
 * counts. One person is named ("Sam"), several are totalled ("2 people").
 * The first line counts distinct projects; the rest count results.
 * Unreadable projects count as failed.
 */
export const summariseResults = (
  action: BulkAction,
  results: BulkResult[]
): string[] => {
  const people = uniqueBy(
    results.map((r) => r.person),
    sameName
  )
  if (people.length === 0) return []
  const who = people.length === 1 ? people[0] : `${people.length} people`

  const count = (...outcomes: Outcome[]): number =>
    results.filter((r) => outcomes.includes(r.outcome)).length
  const doneProjects = new Set(
    results.filter((r) => r.outcome === "done").map((r) => r.projectId)
  ).size
  const failed = count("failed", "unreadable")
  const notRun = count("not-run")

  const lines: string[] = []
  if (action === "add") {
    if (doneProjects > 0) lines.push(`Added ${who} to ${projects(doneProjects)}.`)
    const members = count("already-member")
    if (members > 0) lines.push(`Already a member of ${members}.`)
    const owns = count("owner")
    if (owns > 0) lines.push(`Owns ${owns}.`)
  } else {
    if (doneProjects > 0) {
      lines.push(`Removed ${who} from ${projects(doneProjects)}.`)
    }
    const notMembers = count("not-member")
    if (notMembers > 0) lines.push(`Wasn't a member of ${notMembers}.`)
  }
  if (failed > 0) lines.push(`${failed} failed.`)
  if (notRun > 0) lines.push(`${notRun} not run.`)

  return lines
}
